import { format } from "date-fns";
import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { salesRoutes } from "../../data";
import {
  useSaleCategoryByDateQuery,
} from "../../services/saleApi";
import WareHouseDW from "../Common/CustomSelect/WareHouseDW";
import Header from "../Common/Header/Header";
import LoadingModal from "../Common/Modal/LoadingModal";
import ExportNav from "../Common/Nav/ExportNav";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";
import { ImFileText2 } from "react-icons/im";

const AllCategorySale = () => {
  const auth = signInUser();
  const { aamarId, storeSettings } = auth;
  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [articleSale, setArticleSale] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [loader, setLoader] = useState(true);
  const handleLoaderClose = () => setLoader(false);
  const lang = useSelector((state) => state.languageReducer);

  const [totalSales, setTotalSales] = useState(0);
  const [warehouse, setWarehouse] = useState("allWh");
  const { data, isLoading, isFetching, isSuccess, refetch } =
    useSaleCategoryByDateQuery({
      startDate,
      endDate,
      warehouse,
      aamarId,
    });

  useEffect(() => {
    refetch();
  }, [startDate, endDate, warehouse]);

  useEffect(() => {
    if (auth?.type === "admin") {
      setWarehouse("allWh");
    } else {
      setWarehouse(auth?.warehouse);
    }
  }, []);

  useEffect(() => {
    isFetching ? setLoader(true) : setLoader(false);
  }, [isLoading, isFetching]);

  useEffect(() => {
    let articleSales = [];

    if (data?.length > 0) {
      data?.map(
        (sale) =>
          (articleSales = [
            ...articleSales,
            {
              code: sale?.code,
              name: sale?.name,
              totalQuantity: sale?.totalQuantity,
              totalValue: sale?.totalValue,
              totalMrp: sale?.totalMrp,
              totalTP: sale?.totalTP,
            },
          ])
      );
    }
    setArticleSale(articleSales);

    const csvData = articleSales.map((item) => ({
      "Category Code": item.code,
      "Category Name": item.name,
      "Total Qty": item.totalQuantity,
      "Total MRP Sale": item.totalMrp,
      "Total TP Sale": item.totalTP,
    }));
    setExportData(csvData);

    let total = 0;
    if (articleSales?.length) {
      articleSales.map((sale) => {
        total = total + sale.totalMrp;
      });
    }
    setTotalSales(total);
  }, [isSuccess, isFetching, data]);

  const handleOnchangeWareHouseFrom = (e) => {
    if (e.option !== "no-warehouse") {
      setWarehouse(e.option);
    } else {
      setWarehouse("allWh");
    }
  };
  const preHeader = [
    [`${storeSettings?.storeName || "No-Name"}`],
    [`${storeSettings?.address?.street || "No-Street"}`],
    [
      `${storeSettings?.address?.city || "No-City"}-${
        storeSettings?.address?.post || "No-PostalCode"
      }`,
    ],
    [
      `For the Period: ${format(new Date(startDate), "MM-dd-yyyy")} to ${format(
        new Date(endDate),
        "MM-dd-yyyy"
      )}`,
    ],
    ["All Category Sales Report"],

    [], // Empty row for spacing
  ];

  return (
    <div>
      <LoadingModal
        title={"Please Wait"}
        onShow={loader}
        handleClose={handleLoaderClose}
      ></LoadingModal>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Aamar-Dokan-All-Categories</title>
      </Helmet>

      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideBar></SideBar>
          </div>
          <div className="col-md-10">
            <Header title={lang?.allCategoriesSales}></Header>
            <div className="row mt-3">
              {/* Date Range, Search, Warehouse */}
              <div className="col-md-6">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                  <DatePicker
                    selected={new Date(startDate)}
                    className="form-control"
                    onChange={(date) =>
                      setStartDate(format(new Date(date), "MM-dd-yyyy"))
                    }
                    placeholderText="Start Date"
                  />
                  <DatePicker
                    selected={new Date(endDate)}
                    className="form-control"
                    onChange={(date) =>
                      setEndDate(format(new Date(date), "MM-dd-yyyy"))
                    }
                    placeholderText="End Date"
                  />

                  {auth?.type === "admin" && (
                    <div
                      style={{
                        width: window.innerWidth >= 768 ? 280 : "100%",
                      }}
                    >
                      <WareHouseDW
                        id="warehouse"
                        name="warehouse"
                        handleOnChange={handleOnchangeWareHouseFrom}
                        className="form-control"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Export Navigation */}
              <div className="col-md-6 d-flex justify-content-end align-items-start">
                <ExportNav
                  preheader={preHeader}
                  Routes={salesRoutes}
                  title="All Category Sales"
                  data={exportData}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table
                className="table table-striped align-middle"
                style={{ backgroundColor: "#F8F9FA" }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#F2652D", color: "white" }}>
                    <th>Category Code</th>
                    <th>Category Name</th>
                    <th className="text-center">Total Qty</th>
                    <th className="text-center">Total MRP Sale</th>
                    <th className="text-center">Total TP Sale</th>
                  </tr>
                </thead>
                <tbody>
                  {articleSale?.length > 0 ? (
                    articleSale.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{item.code}</strong>
                        </td>
                        <td>{item.name}</td>
                        <td className="text-center">
                          {new Intl.NumberFormat().format(item.totalQuantity)}
                        </td>
                        <td className="text-center">
                          {new Intl.NumberFormat().format(item.totalMrp)}
                        </td>
                        <td className="text-center">
                          {new Intl.NumberFormat().format(item.totalTP)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          height: "680px",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <div
                          className="d-flex flex-column align-items-center justify-content-center text-muted w-100 h-100 py-5"
                          style={{ minHeight: "100%" }}
                        >
                          <ImFileText2
                            size={40}
                            className="mb-2 text-secondary"
                          />
                          <h6 className="fw-bold mb-0">
                            No All Category Sales Data Found
                          </h6>
                        </div>
                      </td>
                    </tr>
                  )}

                  {articleSale?.length > 0 && (
                    <tr style={{ backgroundColor: "#f1f1f1" }}>
                      <td colSpan={3}></td>
                      <td className="text-center fw-bold">Total</td>
                      <td
                        className="text-center fw-bold"
                        style={{ color: "#F2652D" }}
                      >
                        {new Intl.NumberFormat().format(
                          parseFloat(totalSales).toFixed(2)
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCategorySale;
